"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SVGRenderer = exports.CROSSHAIR_CLASSES = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const lodash_1 = require("lodash");
const model_1 = require("../../../model");
const unit_1 = require("../../../unit");
const angle_1 = require("../../../unit/angle");
const xml_1 = require("../../../xml");
const label_draw_svg_1 = require("../../drawable/label.draw.svg");
const line_draw_svg_1 = require("../../drawable/line.draw.svg");
const marker_draw_svg_1 = require("../../drawable/marker.draw.svg");
const mask_draw_svg_1 = require("../../drawable/mask.draw.svg");
const util_draw_svg_1 = require("../../drawable/util.draw.svg");
const vertical_separation_draw_svg_1 = require("../../drawable/vertical.separation.draw.svg");
const mithril_1 = require("../../mithril");
const theme_1 = require("../../theme");
const crosshair_1 = require("../crosshair");
const rollIndicator_1 = require("../rollIndicator");
const rose_1 = require("../rose");
const svg_style_1 = require("./svg.style");
const timezones_1 = require("./timezones");
exports.CROSSHAIR_CLASSES = {
    innerLine: 'c-Draw-crosshair--innerline',
    outerLine: 'c-Draw-crosshair--outerline',
    distance: 'c-Draw-crosshair--distance'
};
const EXPORT_STRING_OPTIONS = {
    keyMap: { className: 'class' },
    ignoreKeys: ['oncreate', 'onupdate', 'onremove']
};
const DRAWABLE_PRIORITY = {
    hovered: 0,
    selected: 1,
    drawable: 2,
    mask: 3
};
class SVGRenderer {
    static toSVGString(draw, units, timezone, thumbnail = false) {
        const ctx = {
            draw,
            units,
            timezone,
            isThumbnail: thumbnail,
            isInteractive: false,
            isEditable: false,
            isImageDisabled: true,
            scale: SVGRenderer.scaleUI(draw.state, thumbnail),
            isVerticalSeparationEnabled: true
        };
        const vNodes = SVGRenderer.view(ctx);
        return xml_1.IkeXML.Export.asString(vNodes, EXPORT_STRING_OPTIONS);
    }
    static scaleUI(drawState, isThumbnail) {
        if (isThumbnail) {
            return drawState.scale;
        }
        return drawState.scale / drawState.zoom;
    }
    static viewCrosshair(ctx) {
        if (!ctx.draw.state.isCrosshairVisible || ctx.draw.capture.distance == null) {
            return null;
        }
        const resolution = ctx.draw.capture.resolution;
        const center = { x: resolution.width / 2, y: resolution.height / 2 };
        const gap = 15;
        const size = { width: 150, height: 150 };
        return crosshair_1.Crosshair.view(ctx.draw, {
            center,
            size,
            gap,
            units: ctx.units,
            classNames: exports.CROSSHAIR_CLASSES,
            scale: ctx.scale,
            outerStroke: 'rgba(0,0,0,0.5)',
            innerStroke: 'red',
            distance: ctx.draw.geometry3d.getDistance()
        });
    }
    static viewRoll(ctx) {
        var _a, _b, _c;
        const roll = (_c = (_b = (_a = ctx.draw.capture.raw) === null || _a === void 0 ? void 0 : _a.orientation) === null || _b === void 0 ? void 0 : _b.roll) !== null && _c !== void 0 ? _c : null;
        if (!ctx.enableRoll || !ctx.draw.state.isRollVisible || roll === null) {
            return null;
        }
        return rollIndicator_1.RollIndicator.view(ctx.draw, {
            roll,
            width: ctx.draw.capture.resolution.width,
            height: ctx.draw.capture.resolution.height,
            zoomLevel: ctx.draw.state.zoom,
            scale: ctx.scale,
            resHeight: ctx.draw.capture.resolution.height
        });
    }
    static view(ctx) {
        ctx.scale = ctx.scale || this.scaleUI(ctx.draw.state, ctx.isThumbnail);
        const drawState = ctx.draw.drawState;
        const toDraw = drawState.values();
        const data = [];
        const capture = ctx.draw.capture;
        const zoomLevel = ctx.draw.state.zoom;
        const centerPoint = ctx.draw.state.center;
        const viewBox = getViewBox(capture.resolution, centerPoint, zoomLevel);
        data.push(SVGRenderer.viewCrosshair(ctx));
        data.push(SVGRenderer.viewRoll(ctx));
        let nodes = toDraw
            // Identify what type of drawable we're creating
            .map(drawable => {
            const id = drawable.id;
            let kind = model_1.CollectionModelDrawableMask.isMask(drawable) ? 'mask' : 'drawable';
            if (ctx.isInteractive) {
                if (id === ctx.draw.state.selectedId) {
                    kind = 'selected';
                }
                else if (id === ctx.draw.state.hoverId) {
                    kind = 'hovered';
                }
            }
            return {
                kind,
                drawable
            };
        })
            // Sort the drawables so that the the hovered and selected drawables are rendered on top
            .sort((a, b) => DRAWABLE_PRIORITY[b.kind] - DRAWABLE_PRIORITY[a.kind])
            // Get the mithril nodes
            .map(({ drawable, kind }) => {
            return SVGRenderer.renderDrawable(ctx, drawable);
        })
            .filter(Boolean);
        let compassDrawable = drawState.baseOffset || drawState.base || null;
        nodes = [
            ...nodes,
            this.renderOrientationIndicator(ctx, compassDrawable, ctx.showOrientation || false),
            this.renderDateLabel(ctx, viewBox[3], zoomLevel),
            this.renderBearingLabel(ctx, viewBox[2], viewBox[3], zoomLevel)
        ];
        // if it's measurable (trusize image) we should not put this layer on the top of the markers
        // see bug: https://surveylab.atlassian.net/browse/IKE4-3258
        if (!ctx.isThumbnail && !ctx.draw.isIkePhoto) {
            let [x, y, width, height] = [ctx.draw.state.cropX, ctx.draw.state.cropY, ctx.draw.state.cropWidth, ctx.draw.state.cropHeight];
            //if there is no cropping region set, then all the capture is the cropping region
            if (ctx.draw.state.cropWidth === 0 || ctx.draw.state.cropHeight === 0) {
                [x, y, width, height] = [0, 0, capture.resolution.width, capture.resolution.height];
            }
            const cropView = (0, mithril_1.m)('rect', {
                className: theme_1.ClassNames.cropWindow,
                'fill-opacity': 0.0,
                width,
                height,
                x,
                y
            });
            if (zoomLevel > 1) {
                cropView.attrs['c-Draw-draggable'] = 'true';
                cropView.attrs.className += ' ' + theme_1.ClassNames.isMoveable;
                cropView.attrs['c-Draw-draggable'] = 'true';
                cropView.attrs['c-Draw-node-id'] = 'image';
                cropView.attrs['c-Draw-Label'] = util_draw_svg_1.IkeDrawLabel.IMAGE;
                cropView.attrs['c-Draw-tag'] = 'image';
            }
            else {
                cropView.attrs['c-Draw-draggable'] = 'false';
                cropView.attrs.className = theme_1.ClassNames.cropWindow;
                cropView.attrs['c-Draw-draggable'] = 'false';
                cropView.attrs['c-Draw-node-id'] = 'rect';
                //cropView.attrs['c-Draw-Label'] = IkeDrawLabel.IMAGE;
                //cropView.attrs['c-Draw-tag'] = 'rect';
            }
            nodes.push(cropView);
        }
        ctx.draw.log.debug({
            collection: ctx.draw.collection.id,
            capture: ctx.draw.capture.objectId,
            nodes: [...nodes]
        }, 'SVGRenderer: rendering drawable nodes for capture');
        const drawableContainer = (0, mithril_1.m)('g', { className: 'c-Drawables' }, nodes);
        data.push(drawableContainer);
        data.push(SVGRenderer.renderImage(ctx, capture, zoomLevel));
        data.push(SVGRenderer.renderFilters(ctx));
        const rootSVG = (0, mithril_1.m)('svg', {
            className: theme_1.ClassNames.svg,
            style: svg_style_1.SVGStyles[theme_1.ClassNames.svg],
            'data-capture-id': drawState.captureId
        }, data.reverse());
        if (ctx.isInteractive) {
            ctx.draw.boundEvents.lastRenderOptions = ctx;
            rootSVG.attrs.onclick = ctx.draw.boundEvents.click;
            rootSVG.attrs.ondblclick = ctx.draw.boundEvents.doubleclick;
            rootSVG.attrs.onmousedown = ctx.draw.boundEvents.mousedown;
            rootSVG.attrs.onmousemove = ctx.draw.boundEvents.mousemove;
            rootSVG.attrs.onwheel = ctx.draw.boundWheelEvent;
            // No requirement for touch support.
            //rootSVG.attrs.ontouchmove = SVGEventHandler.mousemove.bind(null, this.dc);
            //rootSVG.attrs.ontouchstart = SVGEventHandler.mousedown.bind(null, this.dc);
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isInteractive;
        }
        else {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isStatic;
        }
        if (ctx.draw.isMoving) {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isMoving;
        }
        if (ctx.draw.isLinking) {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isLinking;
        }
        const currentTool = ctx.draw.state.tool;
        if (ctx.isInteractive && currentTool != null) {
            rootSVG.attrs.className += ` ${theme_1.ClassNames.isToolActive} c-Draw-Tool-${currentTool}`;
        }
        if (ctx.isThumbnail) {
            rootSVG.attrs.width = ctx.size.width;
            rootSVG.attrs.height = ctx.size.height;
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isThumbnail;
            rootSVG.attrs.preserveAspectRatio = 'xMinYMin slice';
            rootSVG.attrs.viewBox = getThumbnailViewBox(capture.resolution, ctx.size).join(' ');
            if (zoomLevel > 1) {
                const [x, y, width, height] = viewBox;
                const currentView = (0, mithril_1.m)('rect', {
                    className: theme_1.ClassNames.thumbnailZoomWindow,
                    fill: 'rgba(0,0,0,0.35)',
                    width,
                    height,
                    x,
                    y
                });
                rootSVG.children.push(currentView);
            }
        }
        else {
            rootSVG.attrs.viewBox = viewBox.join(' ');
            rootSVG.attrs.preserveAspectRatio = 'xMinYMin';
        }
        let rootClassName = theme_1.ClassNames.container;
        if (ctx.isThumbnail) {
            rootClassName += ' ' + theme_1.ClassNames.isThumbnail;
        }
        return (0, mithril_1.m)('div', {
            className: rootClassName, style: ctx.draw.orientation === 'landscape' ? {
                display: 'flex',
                'flex-direction': 'column',
                'justify-content': 'center',
                'align-items': 'center'
            } : {}
        }, rootSVG);
    }
    static filterId(dc) {
        return `${dc.id}:brightness`;
    }
    static renderFilters(ctx) {
        // https://github.com/iamvdo/pleeease-filters
        const feFuncAttrs = { type: 'gamma', exponent: ctx.draw.state.brightness };
        return (0, mithril_1.m)('defs', null, [
            (0, mithril_1.m)('filter', { id: SVGRenderer.filterId(ctx.draw) }, [
                (0, mithril_1.m)('feComponentTransfer', { 'color-interpolation-filters': 'sRGB' }, [
                    (0, mithril_1.m)('feFuncR', feFuncAttrs),
                    (0, mithril_1.m)('feFuncG', feFuncAttrs),
                    (0, mithril_1.m)('feFuncB', feFuncAttrs)
                ])
            ])
        ]);
    }
    static renderDateLabel(ctx, height, zoomLevel) {
        if (!ctx.draw.state.isDateLabelVisible) {
            return null;
        }
        const rectHeight = ctx.scale * label_draw_svg_1.LabelSVGDraw.height(1);
        const yPos = (height * zoomLevel) - rectHeight;
        const xOffset = label_draw_svg_1.LabelSVGDraw.Size.width * ctx.scale / 2;
        const node = (0, mithril_1.m)('g', {
            transform: `translate(0, ${yPos})`
        }, [
            (0, mithril_1.m)('rect', {
                x: '0',
                y: '0',
                width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                height: rectHeight,
                fill: 'rgb(224,224,224)',
                'fill-opacity': 0.75,
                stoke: 'none',
                'stroke-width': 0
            }),
            (0, mithril_1.m)('g', { transform: `translate(${xOffset}, 0) scale(${ctx.scale})` }, [
                (0, mithril_1.m)('text', {
                    style: 'font-size: 14px; user-select: none; pointer-events: none;',
                    x: '0',
                    y: '1em',
                    width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                    'text-anchor': 'middle',
                    dy: '0.25em'
                }, formatDateTime(ctx.draw.capture.collectedAt.iso, ctx.timezone))
            ])
        ]);
        return node;
    }
    static renderBearingLabel(ctx, width, height, zoomLevel) {
        if (!ctx.draw.state.isBearingLabelVisible || ctx.draw.capture.orientation == null) {
            return null;
        }
        const rectHeight = ctx.scale * label_draw_svg_1.LabelSVGDraw.height(1);
        const rectWidth = ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width;
        const xOffset = rectWidth / 2;
        const yPos = (height * zoomLevel) - rectHeight;
        const xPos = ((width * zoomLevel) - rectWidth) / 2;
        const node = (0, mithril_1.m)('g', {
            transform: `translate(${xPos}, ${yPos})`
        }, [
            (0, mithril_1.m)('rect', {
                x: '0',
                y: '0',
                width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                height: rectHeight,
                fill: 'rgb(224,224,224)',
                'fill-opacity': 0.75,
                stoke: 'none',
                'stroke-width': 0
            }),
            (0, mithril_1.m)('g', { transform: `translate(${xOffset}, 0)scale(${ctx.scale})` }, [
                (0, mithril_1.m)('text', {
                    style: 'font-size: 14px; user-select: none; pointer-events: none;',
                    x: '0',
                    y: '1em',
                    width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                    'text-anchor': 'middle',
                    dy: '0.25em'
                }, unit_1.IkeUnit.displayAngle(ctx.draw.capture.orientation.bearing, core_api_1.IkeUnitAngle.DEGREE, core_api_1.IkeUnitAngle.RADIAN, 0))
            ])
        ]);
        return node;
    }
    static renderImage(ctx, image, zoomFactor) {
        if (ctx.isImageDisabled) {
            return null;
        }
        if (!ctx.isThumbnail && !ctx.draw.isImageReady(image.objectId)) {
            ctx.draw.loadImage(image.objectId, image.file.url);
            return null;
        }
        let href = image.file.url;
        const width = image.resolution.width;
        const height = image.resolution.height;
        if (ctx.isThumbnail && image.thumbnail) {
            href = image.thumbnail.url;
        }
        const imageNode = (0, mithril_1.m)('image', {
            className: theme_1.ClassNames.image,
            width,
            height,
            x: 0,
            y: 0,
            'xlink:href': href,
            // Stop firefox from grabbing the image
            draggable: false,
            ondragstart: e => e.preventDefault()
        });
        if (zoomFactor > 1) {
            imageNode.attrs.className += ' ' + theme_1.ClassNames.isMoveable;
            imageNode.attrs['c-Draw-draggable'] = 'true';
            imageNode.attrs['c-Draw-node-id'] = 'image';
            imageNode.attrs['c-Draw-Label'] = util_draw_svg_1.IkeDrawLabel.IMAGE;
            imageNode.attrs['c-Draw-tag'] = 'image';
        }
        if (ctx.draw.state.brightness !== 1) {
            imageNode.attrs['filter'] = `url(#${SVGRenderer.filterId(ctx.draw)})`;
        }
        imageNode.attrs.className += ' ' + theme_1.ClassNames.isImageLoaded;
        return imageNode;
    }
    static renderOrientationIndicator(ctx, obj, withOrientationIndicator) {
        if (!withOrientationIndicator || !obj) {
            return null;
        }
        let opts = {
            y: obj.y,
            captureBearing: (0, lodash_1.get)(obj, ['drawableState', 'capture', 'orientation', 'bearing'], null),
            orientationBearing: ctx.orientationBearing,
            color: ctx.indicatorColor
        };
        if (typeof opts.orientationBearing === 'number') {
            const northRotation = 360 - angle_1.IkeAngle.toDegrees(opts.captureBearing);
            const indicatorRotation = northRotation + angle_1.IkeAngle.toDegrees(opts.orientationBearing);
            return (0, mithril_1.m)('svg', {
                class: 'c-Draw-orientation-indicator',
                y: `${opts.y}px`,
                x: '50%',
                style: {
                    overflow: 'visible',
                    'z-index': -10,
                    color: opts.color
                }
            }, (0, mithril_1.m)('g', {
                style: {
                    transform: 'translate3d(-200px, -65px, 1px)'
                }
            }, (0, mithril_1.m)('g', {
                style: {
                    transform: `perspective(300px) rotateX(70deg)`
                }
            }, (0, mithril_1.m)('g', {
                transform: `rotate(${indicatorRotation}, 200, 200)`
            }, rose_1.rose))));
        }
        return null;
    }
    static renderDrawable(ctx, obj) {
        let node;
        if (model_1.CollectionModelDrawableMarker.isMarker(obj)) {
            node = marker_draw_svg_1.MarkerSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableMarkerOffset.isBaseOffset(obj)) {
            node = marker_draw_svg_1.MarkerSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableLabel.isLabel(obj) && obj.parent == null) {
            node = label_draw_svg_1.LabelSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableLine.isLine(obj)) {
            node = line_draw_svg_1.LineSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableMask.isMask(obj)) {
            const maskFetcher = ctx.draw.captureMaskFetcher;
            if (maskFetcher) {
                obj.fetchSvgMask(() => tslib_1.__awaiter(this, void 0, void 0, function* () { return yield maskFetcher(ctx.draw.collection.id, ctx.draw.capture.objectId); })).catch(error => {
                    // We have to catch and log the error here or the app might crash
                    // due to an uncaught promise rejection.
                    ctx.draw.log.error({ error }, 'SVGRenderer: caught error while fetching mask');
                });
            }
            node = mask_draw_svg_1.MaskSVGDraw.view(obj, ctx);
        }
        else if (obj instanceof model_1.CollectionModelDrawableVerticalSeparation) {
            node = vertical_separation_draw_svg_1.VerticalSeparationSVGDraw.view(obj, ctx);
        }
        if (node) {
            node.key = obj.id;
        }
        return node;
    }
}
exports.SVGRenderer = SVGRenderer;
SVGRenderer.Style = svg_style_1.SVGStyles;
SVGRenderer.CSS = theme_1.ClassNames;
function getThumbnailViewBox(resolution, thumbnailSize) {
    const dimensions = scaleCenterCrop(resolution, thumbnailSize);
    const viewX = dimensions.left * 1 / dimensions.scale;
    const viewY = dimensions.top * 1 / dimensions.scale;
    return [-viewX, -viewY, resolution.width + viewX, resolution.height + viewY];
}
function getViewBox(resolution, center, zoomFactor) {
    const zoomWidth = resolution.width / zoomFactor / 2;
    const zoomHeight = resolution.height / zoomFactor / 2;
    if (center.x - zoomWidth < 0) {
        center.x = zoomWidth;
    }
    else if (center.x + zoomWidth > resolution.width) {
        center.x = resolution.width - zoomWidth;
    }
    if (center.y - zoomHeight < 0) {
        center.y = zoomHeight;
    }
    else if (center.y + zoomHeight > resolution.height) {
        center.y = resolution.height - zoomHeight;
    }
    return [center.x - zoomWidth, center.y - zoomHeight, zoomWidth * 2, zoomHeight * 2];
}
function scaleCenterCrop(source, dest) {
    // Compute the scaling factors to fit the new height and width, respectively.
    // To cover the final image, the final scaling will be the bigger
    // of these two.
    const xScale = dest.width / source.width;
    const yScale = dest.height / source.height;
    const scale = Math.max(xScale, yScale);
    // Now get the size of the source.bitmap when scaled
    const scaledWidth = scale * source.width;
    const scaledHeight = scale * source.height;
    // Let's find out the upper left coordinates if the scaled bitmap
    // should be centered in the new size give by the parameters
    const left = (dest.width - scaledWidth) / 2;
    const top = (dest.height - scaledHeight) / 2;
    return {
        top,
        left,
        width: scaledWidth > dest.width ? dest.width : scaledWidth,
        height: scaledHeight > dest.height ? dest.height : scaledHeight,
        scale
    };
}
function formatDateTime(isoDate, timezone) {
    const locale = 'en-GB';
    const searchString = isoDate.slice(0, -5) + '.000Z';
    const date = new Date(searchString);
    const timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const datePart = `${date.toLocaleString(locale, { month: 'short', day: '2-digit', timeZone })}, ${date.toLocaleString(locale, { year: 'numeric', timeZone })} `;
    const output = datePart + ' ' + date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: true, timeZone });
    if (timeZone === 'UTC') {
        return output + ' (UTC)';
    }
    const tzshort = getTimezoneShort(timeZone);
    return `${output} (${tzshort})`;
}
function getTimezoneShort(tz) {
    const result = timezones_1.Timezones.getTimezones().find(({ utc }) => utc.indexOf(tz) > 0);
    if (result === undefined) {
        return 'N/A';
    }
    return result.abbr;
}
//# sourceMappingURL=svg.render.js.map